import React, { useState } from 'react';
import axios from 'axios';
import { ArrowUpRight, MessageCircle, Phone, MapPin, Mail } from 'lucide-react';
import { BRAND, WHATSAPP_LINK } from '../data/mock';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: '', whatsapp: '', company: '', message: '' });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.whatsapp) {
      toast.error('Preencha nome e WhatsApp pra continuar.');
      return;
    }
    setLoading(true);
    try {
      // 1. salvar lead no backend
      await axios.post(`${API}/leads`, {
        name: form.name,
        whatsapp: form.whatsapp,
        company: form.company || null,
        message: form.message || null,
        source: 'site-form',
      });
      toast.success('Mensagem recebida! Vou te chamar já já.');

      // 2. abrir WhatsApp pre-preenchido
      const msg = `Olá! Sou ${form.name}${form.company ? ` (${form.company})` : ''} — ${form.message || 'Vim pelo site da Thesis.'} — Meu WhatsApp: ${form.whatsapp}`;
      window.open(`https://wa.me/${BRAND.phoneRaw}?text=${encodeURIComponent(msg)}`, '_blank');
      setForm({ name: '', whatsapp: '', company: '', message: '' });
    } catch (err) {
      toast.error('Algo deu errado. Tenta de novo ou fala direto no WhatsApp.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contato" className="py-24 md:py-36 relative">
      <div className="thesis-container">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-end border-b border-white/8 pb-8 md:pb-12">
          <div className="md:col-span-2">
            <span className="font-mono-label text-[#8E8B85]">(09) — Contato</span>
          </div>
          <div className="md:col-span-10 reveal">
            <h2 className="font-display-tight text-[44px] md:text-[80px] leading-[0.92] text-[#F5F1E8]">
              Fale comigo <span className="font-serif-italic kinetic-grad">agora.</span>
            </h2>
          </div>
        </div>

        <div className="mt-12 md:mt-20 grid grid-cols-1 md:grid-cols-12 gap-10">
          <aside className="md:col-span-5 reveal">
            <p className="text-[18px] md:text-[20px] text-[#F5F1E8] leading-relaxed max-w-[420px]">
              Resposta em <span className="font-serif-italic text-[#C8FF61]">até 1 hora</span> em horário comercial. Atendo Belo Horizonte e região, mas também clientes do Brasil inteiro 100% remoto.
            </p>

            <div className="mt-9 space-y-5">
              <a href={WHATSAPP_LINK()} target="_blank" rel="noopener noreferrer"
                data-testid="contact-whatsapp-link"
                className="flex items-start gap-4 group cursor-hover">
                <span className="shrink-0 w-12 h-12 rounded-full bg-[#C8FF61] text-[#0A0B0E] flex items-center justify-center group-hover:bg-[#F5F1E8] transition-colors duration-300">
                  <MessageCircle size={18} />
                </span>
                <div>
                  <span className="font-mono-label text-[#8E8B85]">WhatsApp</span>
                  <p className="font-display-tight text-[24px] md:text-[28px] text-[#F5F1E8] leading-none mt-1">
                    {BRAND.phoneFormatted}
                  </p>
                </div>
              </a>
              <div className="flex items-start gap-4">
                <span className="shrink-0 w-12 h-12 rounded-full border border-white/20 text-[#F5F1E8] flex items-center justify-center">
                  <MapPin size={18} />
                </span>
                <div>
                  <span className="font-mono-label text-[#8E8B85]">Localização</span>
                  <p className="text-[16px] text-[#F5F1E8] mt-1">{BRAND.city} · {BRAND.region}</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <span className="shrink-0 w-12 h-12 rounded-full border border-white/20 text-[#F5F1E8] flex items-center justify-center">
                  <Mail size={18} />
                </span>
                <div>
                  <span className="font-mono-label text-[#8E8B85]">E-mail</span>
                  <p className="text-[16px] text-[#F5F1E8] mt-1">{BRAND.email}</p>
                </div>
              </div>
            </div>
          </aside>

          <form onSubmit={handleSubmit} data-testid="contact-lead-form" className="md:col-span-7 reveal grid grid-cols-1 md:grid-cols-2 gap-5 glass-strong rounded-[10px] p-7 md:p-9">
            <Field label="Seu nome *" name="name" value={form.name} onChange={handleChange} placeholder="Como te chamamos?" testId="contact-name-input" />
            <Field label="WhatsApp *" name="whatsapp" value={form.whatsapp} onChange={handleChange} placeholder="(31) 99999-9999" testId="contact-whatsapp-input" />
            <div className="md:col-span-2">
              <Field label="Empresa" name="company" value={form.company} onChange={handleChange} placeholder="Nome do negócio (opcional)" testId="contact-company-input" />
            </div>
            <div className="md:col-span-2">
              <label className="font-mono-label text-[#8E8B85] block mb-2">Mensagem</label>
              <textarea
                name="message" rows={4} value={form.message} onChange={handleChange}
                placeholder="Me conta rápido o que você precisa..."
                data-testid="contact-message-textarea"
                className="w-full bg-transparent border-b border-white/15 focus:border-[#C8FF61] outline-none py-3 text-[16px] text-[#F5F1E8] placeholder:text-[#5C5A55] transition-colors duration-300 resize-none"
              />
            </div>
            <div className="md:col-span-2 mt-3 flex flex-wrap gap-3 items-center">
              <button type="submit" disabled={loading} data-testid="contact-submit-button" className="btn-primary disabled:opacity-60 cursor-hover">
                {loading ? 'Enviando...' : 'Enviar mensagem'}
                <ArrowUpRight size={18} />
              </button>
              <a href={WHATSAPP_LINK()} target="_blank" rel="noopener noreferrer" data-testid="contact-call-now-link" className="btn-secondary cursor-hover">
                <Phone size={16} /> Ligar agora
              </a>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, name, value, onChange, placeholder, testId }) {
  return (
    <div>
      <label className="font-mono-label text-[#8E8B85] block mb-2">{label}</label>
      <input
        name={name} value={value} onChange={onChange} placeholder={placeholder}
        data-testid={testId}
        className="w-full bg-transparent border-b border-white/15 focus:border-[#C8FF61] outline-none py-3 text-[16px] text-[#F5F1E8] placeholder:text-[#5C5A55] transition-colors duration-300"
      />
    </div>
  );
}
